// Generic loader for Art Studio sprite sheets (`npm run art:cli -- export`, sheet + JSON, one animation per file).
// A set is an `index.json` listing sheets; each sheet's JSON frames carry animation, facing label and duration. Frames are
// grouped as anims[name].frames[pose][facing] with 16 or 8 facings. Textures are nearest-neighbour slices of one image.
import { Texture, Rectangle } from 'pixi.js';

/** glow: the same frames keeping only the pixels whose colour is in the set's glow list (mask white, cyan slit, blade edge),
 *  for drawing them unlit above the lighting filter so they stay bright at night. */
export interface SheetAnim { name: string; loop: boolean; ms: number[]; frames: Texture[][]; glow?: Texture[][] }
export interface SpriteSet { anchor: [number, number]; size: [number, number]; facings: string[]; anims: Map<string, SheetAnim> }

/** `base` is a folder URL such as `./art/shingan`; `v` is the build hash (same ?v= rule as main.js, so caches never go stale). */
export async function loadSpriteSet(base: string, v = ''): Promise<SpriteSet> {
  const q = v ? '?v=' + encodeURIComponent(v) : '';
  const get = async (f: string) => { const r = await fetch(`${base}/${f}${q}`); if (!r.ok) throw Error(`${f}: ${r.status}`); return r.json(); };
  const idx = await get('index.json');
  const set: SpriteSet = { anchor: idx.anchor, size: idx.size, facings: idx.facings, anims: new Map() };
  await Promise.all((idx.animations as { name: string; sheet: string; json: string; loop: boolean }[]).map(async a => {
    const meta = await get(a.json);
    const img = new Image(); img.src = `${base}/${a.sheet}${q}`; await img.decode();
    const tex = Texture.from(img); tex.source.scaleMode = 'nearest';
    const gtex = idx.glow?.length ? glowTexture(img, idx.glow) : undefined;
    const n = set.facings.length, frames: Texture[][] = [], glow: Texture[][] = [], ms: number[] = [];
    for (const f of meta.frames as { index: number; x: number; y: number; w: number; h: number; duration: number }[]) {
      const pose = Math.floor(f.index / n), fi = f.index % n;
      (frames[pose] ??= [])[fi] = new Texture({ source: tex.source, frame: new Rectangle(f.x, f.y, f.w, f.h) });
      if (gtex) (glow[pose] ??= [])[fi] = new Texture({ source: gtex.source, frame: new Rectangle(f.x, f.y, f.w, f.h) });
      ms[pose] = f.duration;
    }
    set.anims.set(a.name, { name: a.name, loop: a.loop, ms, frames, glow: gtex ? glow : undefined });
  }));
  return set;
}

function glowTexture(img: HTMLImageElement, colors: string[]): Texture {
  const cv = document.createElement('canvas'); cv.width = img.naturalWidth; cv.height = img.naturalHeight;
  const g = cv.getContext('2d')!; g.drawImage(img, 0, 0);
  const d = g.getImageData(0, 0, cv.width, cv.height), keep = new Set(colors.map(c => parseInt(c.slice(1, 7), 16)));
  for (let i = 0; i < d.data.length; i += 4) if (d.data[i + 3] && !keep.has((d.data[i] << 16) | (d.data[i + 1] << 8) | d.data[i + 2])) d.data[i + 3] = 0;
  g.putImageData(d, 0, 0);
  const tex = Texture.from(cv); tex.source.scaleMode = 'nearest'; return tex;
}

/** Facing slot for a game angle (0 = east, y down; slot 0 = south, slots run S, SSE, SE ... clockwise on the map's compass
 *  from south toward east). `prev` adds hysteresis: stay put until the angle is `hold` of a slot past the boundary. */
export function facingSlot(a: number, n = 16, prev = -1, hold = 0.2): number {
  const step = Math.PI * 2 / n, f = (Math.PI / 2 - a) / step, r = ((Math.round(f) % n) + n) % n;
  if (prev < 0 || r === prev) return r;
  let d = f - prev; d -= Math.round(d / n) * n; // signed slots from the previous one
  return Math.abs(d) < 0.5 + hold ? prev : r;
}

/** Frame index at `t` seconds into an animation; looping ones wrap, others hold the last frame. */
export function frameAt(a: SheetAnim, t: number): number {
  const total = a.ms.reduce((s, m) => s + m, 0) / 1000;
  if (!a.loop && t >= total) return a.frames.length - 1;
  let u = a.loop ? t % total : t;
  for (let i = 0; i < a.ms.length; i++) { u -= a.ms[i] / 1000; if (u < 0) return i; }
  return a.frames.length - 1;
}
